import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorHandlerService, SentryService } from '@y42/error-handling';
import { OrgStore } from '@y42/org/state';
import { ToastService } from '@y42/toast';
import { catchError, delay, firstValueFrom, map, Observable, of, tap } from 'rxjs';
import {
  ChangePasswordHttpModel,
  UserHttpModel,
  UserLanguage,
  UserNotificationsHttpModel,
  UserStoreModel,
  UserUIModel,
} from '../models';
import { UserStore } from '../state';
import { UserHttpService } from './user-http.service';
import { UserScopeStorageService } from './user-scope-storage.service';

export enum ChangePasswordError {
  OldPasswordInvalid = 'old_password.invalid',
  Unknown = 'unknown',
}

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(
    private readonly userHttpService: UserHttpService,
    private readonly userScopeStorageService: UserScopeStorageService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly userStore: UserStore,
    private readonly orgStore: OrgStore,
    private readonly sentry: SentryService,
  ) {}

  async storeActiveUser(): Promise<void> {
    const user = await this.getActiveUser();

    if (!user) {
      return;
    }

    this.userStore.setUser(user);

    this.setSentryUser(user);
  }

  deleteActiveUser(): Observable<void> {
    return this.userHttpService.deleteActiveUser().pipe(
      tap(() => {
        this.toastService.success('Your user has been deleted successfully. Signing out...');
      }),
      delay(1000),
      tap(() => {
        this.router.navigateByUrl('/logout');
      }),
      this.errorHandlerService.catchError('[UserService.deleteUser]: could not delete user', () => of(undefined), {
        toast: 'Could not delete your user',
      }),
    );
  }

  updateActiveUser(user: Partial<UserUIModel>): Observable<boolean> {
    return this.userHttpService.updateActiveUser(user).pipe(
      tap((updatedUser) => {
        this.userStore.updateUser(updatedUser);
        this.toastService.success('Profile updated successfully');
      }),
      map(() => true),
      this.errorHandlerService.catchError('[UserService.updateActiveUser]: could not update user', () => of(false), {
        toast: 'Could not update your user',
      }),
    );
  }

  updateEmailSubscriptions(userNotifications: UserNotificationsHttpModel): Observable<UserHttpModel | void> {
    return this.userHttpService.updateEmailSubscriptions(userNotifications).pipe(
      tap((user) => {
        this.userStore.updateUser(user);
        this.toastService.success('Email subscriptions updated successfully');
      }),
      this.errorHandlerService.catchError(
        '[UserService.updateEmailSubscriptions]: could not update email subscriptions',
        () => of(undefined),
        {
          toast: 'Could not update email subscriptions',
        },
      ),
    );
  }

  changePassword(changePassword: ChangePasswordHttpModel): Observable<ChangePasswordError | void> {
    return this.userHttpService.changePassword(changePassword).pipe(
      tap(() => {
        this.toastService.success('Password changed successfully');
      }),
      this.errorHandlerService.catchError('[UserService.changePassword]: could not change password', (error) => {
        const errorKey = (error as Error).message as ChangePasswordError;
        return of(Object.values(ChangePasswordError).includes(errorKey) ? errorKey : ChangePasswordError.Unknown);
      }),
    );
  }

  resendVerificationEmail(): Observable<void> {
    return this.userHttpService.resendVerificationEmail();
  }

  joinOrg(orgId: number): Observable<void> {
    return this.userHttpService.joinOrg(orgId).pipe(
      this.errorHandlerService.catchError('[OrgService.joinOrg]: could not join org', () => of(undefined), {
        toast: 'Could not join org',
        context: {
          orgId,
        },
      }),
    );
  }

  leaveOrg(orgId: number): Observable<void> {
    return this.userHttpService.leaveOrg(orgId).pipe(
      tap(() => {
        this.orgStore.remove(orgId);

        this.toastService.success('Left org successfully');
      }),
      this.errorHandlerService.catchError('[OrgService.leaveOrg]: could not leave org', () => of(undefined), {
        toast: 'Could not leave org',
        context: {
          orgId,
        },
      }),
    );
  }

  loadVsCodeSettings() {
    return this.userScopeStorageService.load<object | undefined>('vscode-settings');
  }

  updateVsCodeSettings(settings: unknown) {
    return this.userScopeStorageService.save<unknown, object>('vscode-settings', settings);
  }

  getLanguages(): Observable<UserLanguage[]> {
    return this.userHttpService.getLanguages();
  }

  private getActiveUser(): Promise<UserHttpModel | null> {
    return firstValueFrom(
      this.userHttpService.getActiveUser().pipe(
        catchError((err: unknown) => {
          console.error('[UserService.getActiveUser]: could not read user profile', err);
          return of(null);
        }),
      ),
    );
  }

  private setSentryUser(user: UserStoreModel): void {
    this.sentry.setUser({
      id: `${user.id}`,
      username: user.full_name,
      email: user.email,
    });
  }
}
